import type { FirmProfessionalRow } from "../../../Services/IntegratorFirm";

export type ProfessionalsSortKey = "lastName" | "office" | "role";
export type SortDirection = "asc" | "desc";

export interface ProfessionalsSort {
  key: ProfessionalsSortKey;
  direction: SortDirection;
}

export const DEFAULT_PROFESSIONALS_SORT: ProfessionalsSort = {
  key: "lastName",
  direction: "asc",
};

export const SORT_LABEL: Record<ProfessionalsSortKey, string> = {
  lastName: "Last Name",
  office: "Office",
  role: "Role",
};

/** Data Admin before Regular User when ascending. */
const ROLE_RANK: Record<FirmProfessionalRow["role"], number> = {
  "Data Admin": 0,
  "Regular User": 1,
};

const collator = new Intl.Collator("en", { sensitivity: "base", numeric: true });

function byName(a: FirmProfessionalRow, b: FirmProfessionalRow): number {
  return (
    collator.compare(a.lastName, b.lastName) ||
    collator.compare(a.firstName, b.firstName) ||
    collator.compare(a.email, b.email)
  );
}

/**
 * Returns a sorted copy. Office and role group rows and order each group by name. Rows with
 * no office stay last in either direction: the direction orders offices, not the gap.
 */
export function sortProfessionals(
  rows: FirmProfessionalRow[],
  sort: ProfessionalsSort
): FirmProfessionalRow[] {
  const sign = sort.direction === "asc" ? 1 : -1;

  const compare = (a: FirmProfessionalRow, b: FirmProfessionalRow): number => {
    if (sort.key === "office") {
      if (!a.office !== !b.office) {
        return a.office ? -1 : 1;
      }
      return sign * collator.compare(a.office, b.office) || byName(a, b);
    }
    if (sort.key === "role") {
      return sign * (ROLE_RANK[a.role] - ROLE_RANK[b.role]) || byName(a, b);
    }
    return sign * byName(a, b);
  };

  return [...rows].sort(compare);
}

/** A header click: the same key flips direction, another key starts ascending. */
export function nextSort(
  current: ProfessionalsSort,
  key: ProfessionalsSortKey
): ProfessionalsSort {
  if (current.key !== key) {
    return { key, direction: "asc" };
  }
  return { key, direction: current.direction === "asc" ? "desc" : "asc" };
}
